import { formatPhoneNumber } from '@supply-mover-2-0/core';
import {
	PORTAL_SMS_DISCLOSURE,
	type PortalNotificationEventPreferenceDto,
	type PortalNotificationPreferencesDto,
	type PortalNotificationType,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { usePortalAuth } from '../hooks/usePortalAuth';
import {
	canOfferBrowserPush,
	disablePortalBrowserPush,
	enablePortalBrowserPush,
	isPortalBrowserPushOn,
} from '../lib/portalPush';
import {
	getPortalNotificationPreferences,
	updatePortalNotificationPreferences,
} from '../services/portalNotificationService';
import { useLoginModalStore } from '../stores/loginModalStore';

type ChannelKeyModel = 'inApp' | 'email' | 'sms' | 'push';

interface ChannelColumnModel {
	key: ChannelKeyModel;
	label: string;
}

/**
 * PD-16475 — Account → Notifications: which support-request events reach the
 * customer on which channel, a pause-all switch, and browser notifications for
 * this device.
 *
 * Only channels the account can use are shown: Email with an address, Text
 * with a phone number, Browser once a browser is subscribed (and the option to
 * subscribe is hidden inside the storefront app, which has no browser push).
 * A Text box reads as checked only when texts will actually be sent; ticking
 * it records consent next to the disclosure below the grid. A customer who
 * replied STOP is told how to undo it, because the app cannot.
 *
 * Every change saves as it is made.
 */
export function NotificationSettingsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [prefs, setPrefs] = useState<PortalNotificationPreferencesDto | null>(
		null,
	);
	const [loadFailed, setLoadFailed] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const offerPush = canOfferBrowserPush();
	const [browserOn, setBrowserOn] = useState(false);
	const [pushMessage, setPushMessage] = useState<string | null>(null);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const load = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		try {
			const [loaded, on] = await Promise.all([
				getPortalNotificationPreferences(token),
				isPortalBrowserPushOn(),
			]);
			setPrefs(loaded);
			setBrowserOn(on);
			setLoadFailed(false);
		} catch {
			setLoadFailed(true);
		}
	}, [token]);

	useEffect(() => {
		void load();
	}, [load]);

	const save = async (
		body: Parameters<typeof updatePortalNotificationPreferences>[1],
	): Promise<void> => {
		setSaving(true);
		setError(null);
		try {
			setPrefs(await updatePortalNotificationPreferences(token, body));
		} catch {
			setError('Your change could not be saved. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const toggleChannel = (
		type: PortalNotificationType,
		channel: ChannelKeyModel,
		on: boolean,
	): void => {
		void save({ events: [{ notificationType: type, [channel]: on }] });
	};

	const turnOnBrowser = async (): Promise<void> => {
		setPushMessage(null);
		const enabled = await enablePortalBrowserPush().catch(() => false);
		if (!enabled) {
			setPushMessage(
				typeof Notification !== 'undefined' &&
					Notification.permission === 'denied'
					? 'Your browser is blocking notifications for this site. Allow them in your browser settings, then try again.'
					: 'Browser notifications are not available right now.',
			);
			return;
		}
		await load();
	};

	const turnOffBrowser = async (): Promise<void> => {
		setPushMessage(null);
		try {
			await disablePortalBrowserPush();
		} catch {
			setPushMessage('Browser notifications could not be turned off.');
		}
		await load();
	};

	if (!isAuthenticated) {
		return <div />;
	}

	const columns: Array<ChannelColumnModel> = [
		{ key: 'inApp', label: 'In app' },
	];
	if (prefs?.available.email === true) {
		columns.push({ key: 'email', label: 'Email' });
	}
	if (prefs?.available.sms === true) {
		columns.push({ key: 'sms', label: 'Text' });
	}
	if (prefs?.available.push === true) {
		columns.push({ key: 'push', label: 'Browser' });
	}

	const smsStatus = prefs?.smsStatus ?? null;

	return (
		<div className="px-4 py-4 max-w-3xl mx-auto">
			<MobilePageHeader title="Notifications" backPath="/account" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-default mb-4">
				{t('Notifications')}
			</h1>

			{prefs == null ? (
				loadFailed ? (
					<p className="text-sm text-danger">
						{t('Your notification settings could not be loaded.')}
					</p>
				) : (
					<div className="animate-pulse space-y-4">
						<div className="h-16 bg-brand-input rounded-xl" />
						<div className="h-40 bg-brand-input rounded-xl" />
					</div>
				)
			) : (
				<div className="space-y-6">
					<section className="bg-brand-surface border border-brand-border rounded-xl p-4">
						<div className="flex items-center gap-3">
							<div className="flex-1 min-w-0">
								<div className="text-sm font-medium text-default">
									{t('Pause all notifications')}
								</div>
							</div>
							<button
								type="button"
								role="switch"
								aria-checked={prefs.paused}
								aria-label={t('Pause all notifications')}
								disabled={saving}
								onClick={() => {
									void save({ paused: !prefs.paused });
								}}
								className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
									prefs.paused
										? 'bg-portal-accent'
										: 'bg-brand-input border border-brand-border'
								}`}
							>
								<span
									className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${
										prefs.paused ? 'translate-x-5' : 'translate-x-0.5'
									}`}
								/>
							</button>
						</div>
						<p className="mt-1 text-xs text-brand-muted">
							{t(
								'While paused you get no notifications on any channel. Your choices below are kept.',
							)}
						</p>
					</section>

					<section
						className={`bg-brand-surface border border-brand-border rounded-xl overflow-hidden ${
							prefs.paused ? 'opacity-60' : ''
						}`}
					>
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b border-subtle">
										<th className="text-left font-medium text-brand-muted px-4 py-3">
											{t('Support requests')}
										</th>
										{columns.map((column) => (
											<th
												key={column.key}
												className="font-medium text-brand-muted px-3 py-3 text-center whitespace-nowrap"
											>
												{t(column.label)}
											</th>
										))}
									</tr>
								</thead>
								<tbody className="divide-y divide-subtle">
									{prefs.events.map((event) => (
										<EventRow
											key={event.notificationType}
											event={event}
											columns={columns}
											disabled={saving || prefs.paused}
											onToggle={toggleChannel}
										/>
									))}
								</tbody>
							</table>
						</div>

						<div className="px-4 py-3 border-t border-subtle space-y-1">
							{prefs.emailAddress != null && (
								<p className="text-xs text-brand-muted">
									{t('Email goes to {address}.').replace(
										'{address}',
										t(prefs.emailAddress),
									)}
								</p>
							)}
							{smsStatus?.phone != null && (
								<>
									<p className="text-xs text-brand-muted">
										{t('Texts go to {phone}.').replace(
											'{phone}',
											formatPhoneNumber(smsStatus.phone),
										)}
									</p>
									<p className="text-xs text-brand-muted">
										{t(PORTAL_SMS_DISCLOSURE)}
									</p>
								</>
							)}
							{smsStatus?.optedOut === true && (
								<p className="text-xs text-warning">
									{smsStatus.startNumber != null
										? t(
												'You replied STOP earlier. Text START to {number} to turn texts back on.',
											).replace(
												'{number}',
												formatPhoneNumber(smsStatus.startNumber),
											)
										: t(
												'You replied STOP earlier. Text START to our number to turn texts back on.',
											)}
								</p>
							)}
						</div>
					</section>

					{offerPush && (
						<section className="bg-brand-surface border border-brand-border rounded-xl p-4">
							<div className="text-sm font-medium text-default">
								{t('Browser notifications')}
							</div>
							<p className="mt-1 text-xs text-brand-muted">
								{browserOn
									? t(
											'This browser shows your notifications even when the store is closed.',
										)
									: t(
											'Get your notifications in this browser, even when the store is closed.',
										)}
							</p>
							<button
								type="button"
								onClick={() => {
									void (browserOn ? turnOffBrowser() : turnOnBrowser());
								}}
								className={
									browserOn
										? 'mt-3 inline-flex items-center px-3 py-2 text-sm text-strong border border-brand-border rounded-lg hover:bg-brand-input'
										: 'mt-3 inline-flex items-center rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors'
								}
							>
								{browserOn
									? t('Turn off browser notifications on this device')
									: t('Turn on browser notifications')}
							</button>
							{pushMessage != null && (
								<p className="mt-2 text-xs text-brand-muted">
									{t(pushMessage)}
								</p>
							)}
						</section>
					)}

					{error != null && <p className="text-sm text-danger">{t(error)}</p>}
				</div>
			)}
		</div>
	);
}

function EventRow({
	event,
	columns,
	disabled,
	onToggle,
}: {
	event: PortalNotificationEventPreferenceDto;
	columns: ReadonlyArray<ChannelColumnModel>;
	disabled: boolean;
	onToggle: (
		type: PortalNotificationType,
		channel: ChannelKeyModel,
		on: boolean,
	) => void;
}): JSX.Element {
	const { t } = useTranslation();
	return (
		<tr>
			<td className="px-4 py-3 align-top">
				<div className="font-medium text-default">{t(event.label)}</div>
				<div className="text-xs text-brand-muted mt-0.5">
					{t(event.description)}
				</div>
			</td>
			{columns.map((column) => (
				<td key={column.key} className="px-3 py-3 text-center align-top">
					<input
						type="checkbox"
						aria-label={`${t(event.label)}: ${t(column.label)}`}
						checked={event[column.key]}
						disabled={disabled}
						onChange={(e) => {
							onToggle(event.notificationType, column.key, e.target.checked);
						}}
						className="w-4 h-4 text-portal-accent"
					/>
				</td>
			))}
		</tr>
	);
}
